import React, { useState, useEffect } from 'react';
import { getIdToken } from './auth';
import './AdminPanel.css';

function AdminPanel() {
  const [adminSubTab, setAdminSubTab] = useState('users'); // 'users' | 'prompts'
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ show: false, action: '', user: null });
  // Prompts & Models
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState('');

  // Get API Gateway URL from window.amplifyConfig
  const API_URL = window.amplifyConfig?.API?.adminApi?.endpoint || '';

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (adminSubTab === 'prompts') {
      loadConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminSubTab]);

  const getAuthToken = async () => {
    try {
      const token = await getIdToken();
      return token;
    } catch (err) {
      console.error('Error getting auth token:', err);
      return null;
    }
  };

  const loadConfig = async () => {
    setConfigLoading(true);
    setConfigError('');
    try {
      if (!API_URL || !API_URL.startsWith('http')) {
        setConfigError('Admin API URL is not configured. Re-deploy the application (./cicd-deploy.sh) to inject the API Gateway endpoint.');
        setConfigLoading(false);
        return;
      }
      const token = await getAuthToken();
      if (!token) {
        setConfigError('Failed to get authentication token');
        setConfigLoading(false);
        return;
      }
      const response = await fetch(`${API_URL}/admin/config`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to load config: ${response.status}`);
      }
      const data = await response.json();
      setConfig(data);
    } catch (err) {
      console.error('Error loading config:', err);
      const isNetworkError = err.message === 'Failed to fetch' || err.name === 'TypeError';
      const message = isNetworkError
        ? 'Could not reach the admin API. Check that the UI Admin stack is deployed with GET /admin/config endpoint and CORS is configured.'
        : (err.message || 'Failed to load prompts & models');
      setConfigError(message);
    } finally {
      setConfigLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setSuccess(`Copied ${label}`);
      setTimeout(() => setSuccess(''), 2000);
    }).catch(() => {});
  };

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Failed to get authentication token');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/admin/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to load users: ${response.status}`);
      }

      const data = await response.json();
      setUsers(data.users || []);
      setSuccess(`Loaded ${data.total || 0} users`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (email) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Failed to get authentication token');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/admin/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to delete user: ${response.status}`);
      }

      setSuccess(`User ${email} deleted successfully`);
      
      // Reload users list
      setTimeout(() => loadUsers(), 1000);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Failed to delete user');
      setLoading(false);
    }
  };

  const handleDisableUser = async (email) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Failed to get authentication token');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/admin/users/${encodeURIComponent(email)}/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to disable user: ${response.status}`);
      }

      setSuccess(`User ${email} disabled successfully`);
      
      // Reload users list
      setTimeout(() => loadUsers(), 1000);
    } catch (err) {
      console.error('Error disabling user:', err);
      setError(err.message || 'Failed to disable user');
      setLoading(false);
    }
  };

  const handleEnableUser = async (email) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Failed to get authentication token');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/admin/users/${encodeURIComponent(email)}/enable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to enable user: ${response.status}`);
      }

      setSuccess(`User ${email} enabled successfully`);
      
      // Reload users list
      setTimeout(() => loadUsers(), 1000);
    } catch (err) {
      console.error('Error enabling user:', err);
      setError(err.message || 'Failed to enable user');
      setLoading(false);
    }
  };

  const showConfirmDialog = (action, user) => {
    setConfirmDialog({ show: true, action, user });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog({ show: false, action: '', user: null });
  };

  const confirmAction = () => {
    const { action, user } = confirmDialog;
    hideConfirmDialog();

    if (action === 'delete') {
      handleDeleteUser(user.email);
    } else if (action === 'disable') {
      handleDisableUser(user.email);
    } else if (action === 'enable') {
      handleEnableUser(user.email);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED':
        return '#10b981'; // Green
      case 'UNCONFIRMED':
        return '#f59e0b'; // Yellow
      case 'FORCE_CHANGE_PASSWORD':
        return '#3b82f6'; // Blue
      default:
        return '#6b7280'; // Gray
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-subtabs">
        <button
          className={`admin-subtab ${adminSubTab === 'users' ? 'active' : ''}`}
          onClick={() => setAdminSubTab('users')}
        >
          👥 Users
        </button>
        <button
          className={`admin-subtab ${adminSubTab === 'prompts' ? 'active' : ''}`}
          onClick={() => setAdminSubTab('prompts')}
        >
          📋 Prompts & Models
        </button>
      </div>

      {adminSubTab === 'users' && (
        <>
          <div className="admin-header">
            <h2>👥 User Management</h2>
            <button 
              onClick={loadUsers} 
              disabled={loading}
              className="refresh-btn"
            >
              {loading ? '⏳' : '🔄'} Refresh
            </button>
          </div>

          {error && (
            <div className="admin-message error">
              ❌ {error}
            </div>
          )}

          {success && (
            <div className="admin-message success">
              ✅ {success}
            </div>
          )}

          <div className="admin-stats">
        <div className="stat-card">
          <div className="stat-value">{users.length}</div>
          <div className="stat-label">Total Users</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{users.filter(u => u.status === 'CONFIRMED').length}</div>
          <div className="stat-label">Confirmed</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{users.filter(u => u.enabled).length}</div>
          <div className="stat-label">Enabled</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{users.filter(u => u.groups?.includes('Admins')).length}</div>
          <div className="stat-label">Admins</div>
        </div>
      </div>

      {loading && users.length === 0 ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : users.length === 0 ? (
        <div className="empty-state">
          <p>👥 No users found</p>
        </div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>Enabled</th>
                <th>Groups</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.username}>
                  <td className="user-email">{user.email}</td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(user.status) }}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td>
                    <span className={`enabled-badge ${user.enabled ? 'enabled' : 'disabled'}`}>
                      {user.enabled ? '✓ Yes' : '✗ No'}
                    </span>
                  </td>
                  <td>
                    {user.groups && user.groups.length > 0 ? (
                      <div className="groups-list">
                        {user.groups.map(g => (
                          <span key={g} className="group-badge">{g}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="no-groups">—</span>
                    )}
                  </td>
                  <td className="user-date">{formatDate(user.created)}</td>
                  <td className="user-actions">
                    {user.enabled ? (
                      <button
                        onClick={() => showConfirmDialog('disable', user)}
                        className="action-btn disable-btn"
                        disabled={loading}
                        title="Disable user"
                      >
                        🚫 Disable
                      </button>
                    ) : (
                      <button
                        onClick={() => showConfirmDialog('enable', user)}
                        className="action-btn enable-btn"
                        disabled={loading}
                        title="Enable user"
                      >
                        ✅ Enable
                      </button>
                    )}
                    <button
                      onClick={() => showConfirmDialog('delete', user)}
                      className="action-btn delete-btn"
                      disabled={loading}
                      title="Delete user"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.show && (
        <div className="modal-overlay" onClick={hideConfirmDialog}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ Confirm Action</h3>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to <strong>{confirmDialog.action}</strong> user:
              </p>
              <p className="confirm-email">{confirmDialog.user?.email}</p>
              {confirmDialog.action === 'delete' && (
                <p className="warning-text">
                  ⚠️ This action cannot be undone. The user will be permanently deleted.
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button onClick={hideConfirmDialog} className="modal-btn cancel-btn">
                Cancel
              </button>
              <button onClick={confirmAction} className="modal-btn confirm-btn">
                {confirmDialog.action === 'delete' ? 'Delete' : 
                 confirmDialog.action === 'disable' ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {adminSubTab === 'prompts' && (
        <>
          <div className="admin-header">
            <h2>📋 Prompts & Models</h2>
            <button
              onClick={loadConfig}
              disabled={configLoading}
              className="refresh-btn"
            >
              {configLoading ? '⏳' : '🔄'} Refresh
            </button>
          </div>
          {success && (
            <div className="admin-message success">✅ {success}</div>
          )}
          {configError && (
            <div className="admin-message error">❌ {configError}</div>
          )}
          {configLoading && !config ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading config...</p>
            </div>
          ) : config ? (
            <div className="config-section">
              <h3 className="config-heading">Prompts (read-only)</h3>
              <table className="config-table">
                <tbody>
                  {config.prompts?.htmlDiagramArn !== undefined && (
                    <tr>
                      <td className="config-label">Fusion HTML Diagram Prompt</td>
                      <td className="config-value">
                        <code>{config.prompts.htmlDiagramArn || '—'}</code>
                        {config.prompts.htmlDiagramArn && (
                          <button type="button" className="copy-btn" onClick={() => copyToClipboard(config.prompts.htmlDiagramArn, 'Fusion HTML Diagram Prompt ARN')} title="Copy">📋</button>
                        )}
                      </td>
                    </tr>
                  )}
                  {config.prompts?.markdownRunbookArn !== undefined && (
                    <tr>
                      <td className="config-label">Fusion Markdown Runbook Prompt</td>
                      <td className="config-value">
                        <code>{config.prompts.markdownRunbookArn || '—'}</code>
                        {config.prompts.markdownRunbookArn && (
                          <button type="button" className="copy-btn" onClick={() => copyToClipboard(config.prompts.markdownRunbookArn, 'Fusion Markdown Runbook Prompt ARN')} title="Copy">📋</button>
                        )}
                      </td>
                    </tr>
                  )}
                  {config.prompts?.wfCoreHtmlArn !== undefined && (
                    <tr>
                      <td className="config-label">WF Core HTML Prompt</td>
                      <td className="config-value">
                        <code>{config.prompts.wfCoreHtmlArn || '—'}</code>
                        {config.prompts.wfCoreHtmlArn && (
                          <button type="button" className="copy-btn" onClick={() => copyToClipboard(config.prompts.wfCoreHtmlArn, 'WF Core HTML Prompt ARN')} title="Copy">📋</button>
                        )}
                      </td>
                    </tr>
                  )}
                  {config.prompts?.wfCoreMarkdownArn !== undefined && (
                    <tr>
                      <td className="config-label">WF Core Markdown Prompt</td>
                      <td className="config-value">
                        <code>{config.prompts.wfCoreMarkdownArn || '—'}</code>
                        {config.prompts.wfCoreMarkdownArn && (
                          <button type="button" className="copy-btn" onClick={() => copyToClipboard(config.prompts.wfCoreMarkdownArn, 'WF Core Markdown Prompt ARN')} title="Copy">📋</button>
                        )}
                      </td>
                    </tr>
                  )}
                  {config.prompts?.wfCoreSynthesizerArn !== undefined && (
                    <tr>
                      <td className="config-label">WF Core Synthesizer Prompt</td>
                      <td className="config-value">
                        <code>{config.prompts.wfCoreSynthesizerArn || '—'}</code>
                        {config.prompts.wfCoreSynthesizerArn && (
                          <button type="button" className="copy-btn" onClick={() => copyToClipboard(config.prompts.wfCoreSynthesizerArn, 'WF Core Synthesizer Prompt ARN')} title="Copy">📋</button>
                        )}
                      </td>
                    </tr>
                  )}
                  {config.prompts?.htmlRunbookArn !== undefined && (
                    <tr>
                      <td className="config-label">CSC Final Runbook Prompt</td>
                      <td className="config-value">
                        <code>{config.prompts.htmlRunbookArn || '—'}</code>
                        {config.prompts.htmlRunbookArn && (
                          <button type="button" className="copy-btn" onClick={() => copyToClipboard(config.prompts.htmlRunbookArn, 'CSC Final Runbook Prompt ARN')} title="Copy">📋</button>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <h3 className="config-heading">Model versions (read-only)</h3>
              <table className="config-table">
                <tbody>
                  {config.models?.htmlDiagramModelId !== undefined && (
                    <tr>
                      <td className="config-label">Fusion HTML Diagram Model</td>
                      <td className="config-value">
                        <code>{config.models.htmlDiagramModelId || '—'}</code>
                        {config.models.htmlDiagramModelId && (
                          <button type="button" className="copy-btn" onClick={() => copyToClipboard(config.models.htmlDiagramModelId, 'Fusion HTML Diagram model')} title="Copy">📋</button>
                        )}
                      </td>
                    </tr>
                  )}
                  {config.models?.markdownRunbookModelId !== undefined && (
                    <tr>
                      <td className="config-label">Fusion Markdown Model</td>
                      <td className="config-value">
                        <code>{config.models.markdownRunbookModelId || '—'}</code>
                        {config.models.markdownRunbookModelId && (
                          <button type="button" className="copy-btn" onClick={() => copyToClipboard(config.models.markdownRunbookModelId, 'Fusion Markdown model')} title="Copy">📋</button>
                        )}
                      </td>
                    </tr>
                  )}
                  {config.models?.htmlRunbookModelId !== undefined && (
                    <tr>
                      <td className="config-label">Fusion HTML Runbook Model</td>
                      <td className="config-value">
                        <code>{config.models.htmlRunbookModelId || '—'}</code>
                        {config.models.htmlRunbookModelId && (
                          <button type="button" className="copy-btn" onClick={() => copyToClipboard(config.models.htmlRunbookModelId, 'Fusion HTML Runbook model')} title="Copy">📋</button>
                        )}
                      </td>
                    </tr>
                  )}
                  {config.models?.wfCoreHtmlModelId !== undefined && (
                    <tr>
                      <td className="config-label">WF Core HTML Model</td>
                      <td className="config-value">
                        <code>{config.models.wfCoreHtmlModelId || '—'}</code>
                        {config.models.wfCoreHtmlModelId && (
                          <button type="button" className="copy-btn" onClick={() => copyToClipboard(config.models.wfCoreHtmlModelId, 'WF Core HTML model')} title="Copy">📋</button>
                        )}
                      </td>
                    </tr>
                  )}
                  {config.models?.wfCoreMarkdownModelId !== undefined && (
                    <tr>
                      <td className="config-label">WF Core Markdown Model</td>
                      <td className="config-value">
                        <code>{config.models.wfCoreMarkdownModelId || '—'}</code>
                        {config.models.wfCoreMarkdownModelId && (
                          <button type="button" className="copy-btn" onClick={() => copyToClipboard(config.models.wfCoreMarkdownModelId, 'WF Core Markdown model')} title="Copy">📋</button>
                        )}
                      </td>
                    </tr>
                  )}
                  {config.models?.wfCoreSynthesizerModelId !== undefined && (
                    <tr>
                      <td className="config-label">WF Core Synthesizer Model</td>
                      <td className="config-value">
                        <code>{config.models.wfCoreSynthesizerModelId || '—'}</code>
                        {config.models.wfCoreSynthesizerModelId && (
                          <button type="button" className="copy-btn" onClick={() => copyToClipboard(config.models.wfCoreSynthesizerModelId, 'WF Core Synthesizer model')} title="Copy">📋</button>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No config available. Deploy the stack with prompt/model parameters to see values here.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminPanel;

